/**
 * Utility functions for accordion state management
 */

/**
 * Toggles all accordion items between expanded and collapsed state
 * @param currentItems - Array of currently expanded item indices
 * @param totalGroups - Total number of accordion groups
 * @returns Array of item indices after toggle
 */
export const toggleAllAccordionItems = (currentItems: string[], totalGroups: number): string[] => {
  const allItemsExpanded = currentItems.length === totalGroups;

  if (allItemsExpanded) {
    // Collapse all
    return [];
  } else {
    // Expand all - create array of all indices as strings
    return Array.from({ length: totalGroups }, (_, index) => index.toString());
  }
};

/**
 * Checks if all accordion items are expanded
 * @param expandedItems - Array of currently expanded item indices
 * @param totalGroups - Total number of accordion groups
 * @returns True if all items are expanded
 */
export const areAllAccordionItemsExpanded = (
  expandedItems: string[],
  totalGroups: number,
): boolean => {
  return expandedItems.length === totalGroups;
};

/**
 * Creates an array of all accordion indices as strings
 * @param totalGroups - Total number of accordion groups
 * @returns Array of all indices as strings
 */
export const createAllAccordionIndices = (totalGroups: number): string[] => {
  return Array.from({ length: totalGroups }, (_, index) => index.toString());
};

/**
 * Toggles a single accordion item in the expanded items array
 * @param expandedItems - Array of currently expanded item indices
 * @param itemIndex - Index of the item to toggle
 * @returns New array with the item toggled
 */
export const toggleAccordionItem = (expandedItems: string[], itemIndex: string): string[] => {
  const isExpanded = expandedItems.includes(itemIndex);

  if (isExpanded) {
    // Remove the item (collapse)
    return expandedItems.filter(item => item !== itemIndex);
  } else {
    // Add the item (expand)
    return [...expandedItems, itemIndex];
  }
};

/**
 * Initializes accordion state based on default expansion preference
 * @param groupCount - Number of accordion groups
 * @param defaultExpanded - Whether to start with all items expanded
 * @returns Initial array of expanded item indices
 */
export const initializeAccordionState = (
  groupCount: number,
  defaultExpanded: boolean = false,
): string[] => {
  return defaultExpanded ? createAllAccordionIndices(groupCount) : [];
};

/**
 * Returns every ancestor prefix of a dotted field path, shallowest first.
 * These are the `data-accordion-path` values of the nested accordions that
 * must be open for the field to be visible.
 *   "drug.0.coverage.copay" → ["drug", "drug.0", "drug.0.coverage"]
 * @param fieldPath - Dotted field path (a top-level path yields no ancestors)
 */
export const getAccordionAncestorPaths = (fieldPath: string): string[] => {
  const segments = fieldPath.split('.');
  return segments.slice(0, -1).map((_, i) => segments.slice(0, i + 1).join('.'));
};

/** Yield once so React can mount the content of a just-opened accordion level. */
const nextFrame = (): Promise<void> =>
  new Promise(resolve =>
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame(() => resolve())
      : setTimeout(resolve, 16),
  );

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/** Pause between opening the accordions and scrolling, so the open animations
 * settle and the field's final position is stable before it is centered. */
const DEFAULT_SCROLL_DELAY_MS = 1000;

/**
 * Expands every collapsed nested accordion along a dotted field path, then scrolls
 * the field into view. The nested levels (entity arrays, array items, object
 * subsections) each own their expand state locally, so this walks the DOM instead:
 * every nested accordion item renders `data-accordion-path`, and clicking its
 * trigger routes through the owning component's state. Levels are opened
 * shallowest-first, yielding a frame between clicks because a closed level's
 * children are not mounted until it opens. Once all levels are open, waits
 * `scrollDelayMs` before scrolling so the layout has settled.
 * @param fieldPath - Dotted path of the target field (matches its `data-field-id`)
 * @param scrollDelayMs - Wait between expansion and the scroll (default 1s)
 * @returns Whether the target field element was found and scrolled to
 */
export const expandAccordionAncestorsAndScrollToField = async (
  fieldPath: string,
  scrollDelayMs: number = DEFAULT_SCROLL_DELAY_MS,
): Promise<boolean> => {
  for (const ancestorPath of getAccordionAncestorPaths(fieldPath)) {
    const item = document.querySelector(`[data-accordion-path="${ancestorPath}"]`);
    if (!item || item.getAttribute('data-state') !== 'closed') continue;
    // A closed item's content is unmounted, so the only stateful button left
    // inside it is its own trigger (action buttons carry no data-state).
    item.querySelector<HTMLButtonElement>('button[data-state="closed"]')?.click();
    await nextFrame();
  }
  const target = document.querySelector(`[data-field-id="${fieldPath}"]`);
  if (!target) return false;
  await delay(scrollDelayMs);
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return true;
};
